/**
 * A escala horizontal ajustável dos gráficos de resultados do CPTu (onda 347; spec 10 §4.8, R12) — contas puras, sem
 * DOM e sem React (o `localStorage` só por `try`, porque pode faltar).
 *
 * Por grandeza (a chave da trilha ou da sobreposição: `qt`, `fs`, `u2`…), em todos os gráficos de resultados da tela —
 * os perfis da vertical e da campanha, os gráficos sobre a seção 2D e as fitas do 3D:
 *  · o tipo do eixo, linear ou log — o log só para grandeza sempre positiva [esc.log-permitido];
 *  · os limites, automáticos (os de hoje: o domínio dos dados) ou fixos, com o mínimo e o máximo nas unidades da
 *    grandeza; valor fora dos limites fixos é cortado na borda, com a marca «▸» de que passou [esc.corte];
 *  · as divisões: as marcas de hoje, ou o passo digitado (só no linear).
 *
 * Fica guardada no aparelho (`labmanager.cptu.escalas` no LabManager, `portal.cptu.escalas` no Portal), nunca no banco:
 * não muda o relatório (o PDF segue as escalas do padrão do relatório CPTu, 337) nem o que o outro vê.
 */
import { marcas } from './escalas';
import type { EscalaDoEixo } from './escalas';

/** A escala de um gráfico. `passo` null: as divisões automáticas. */
export interface EscalaDoGrafico {
  tipo: EscalaDoEixo;
  limites: 'auto' | { min: number; max: number };
  passo: number | null;
}

/** A chave do aparelho no LabManager. */
export const CHAVE_DAS_ESCALAS = 'labmanager.cptu.escalas';
/** A chave do aparelho no Portal (o cliente ajusta só na tela dele). */
export const CHAVE_DAS_ESCALAS_DO_PORTAL = 'portal.cptu.escalas';

/**
 * As grandezas que aceitam o eixo log: as sempre positivas — ESCOLHA DA TELA, SEM FONTE [esc.log-permitido].
 * (O u₂, o Bq, o Ic e o Qtn podem ser zero ou negativos, ou já são uma razão de logaritmos.)
 */
export const GRANDEZAS_COM_LOG: readonly string[] = Object.freeze(['qc', 'qt', 'fs', 'rf', 'su']);

/** «qc, qt, fs, Rf e Su», como a tela escreve. */
const COM_LOG_ESCRITAS = 'qc, qt, fs, Rf e Su';

/** O máximo de divisões que o passo digitado desenha (mais que isso não se lê; o passo não se aplica). */
export const DIVISOES_MAXIMAS = 50;

/** A escala de hoje daquela grandeza: linear, limites automáticos, as marcas de hoje (todos os gráficos são lineares). */
export function ESCALA_AUTOMATICA(_grandeza: string): EscalaDoGrafico {
  return { tipo: 'linear', limites: 'auto', passo: null };
}

/** A escala não muda nada do automático. */
export function escalaEhAutomatica(e: EscalaDoGrafico | null | undefined): boolean {
  return !e || (e.tipo === 'linear' && e.limites === 'auto' && e.passo === null);
}

/** Os limites fixos, ou null. */
export function limitesFixos(e: EscalaDoGrafico | null | undefined): { min: number; max: number } | null {
  return e && e.limites !== 'auto' ? e.limites : null;
}

/** O que está errado na escala (a frase da tela), ou null quando ela vale. */
export function validarEscala(e: EscalaDoGrafico, grandeza: string): string | null {
  if (e.tipo !== 'linear' && e.tipo !== 'log') return 'o tipo do eixo é linear ou log';
  if (e.tipo === 'log' && !GRANDEZAS_COM_LOG.includes(grandeza)) return `o eixo log só vale para ${COM_LOG_ESCRITAS}`;
  if (e.limites !== 'auto') {
    const { min, max } = e.limites;
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 'o mínimo e o máximo têm de ser números';
    if (!(min < max)) return 'o mínimo tem de ser menor que o máximo';
    if (e.tipo === 'log' && !(min > 0)) return 'no eixo log o mínimo tem de ser maior que zero';
  }
  if (e.passo !== null) {
    if (e.tipo === 'log') return 'as divisões digitadas só valem no eixo linear';
    if (!Number.isFinite(e.passo) || !(e.passo > 0)) return 'as divisões têm de ser maiores que zero';
    if (e.limites !== 'auto' && (e.limites.max - e.limites.min) / e.passo > DIVISOES_MAXIMAS) {
      return `divisões demais: no máximo ${DIVISOES_MAXIMAS} no eixo`;
    }
  }
  return null;
}

/**
 * O domínio do eixo: com os limites fixos, [mínimo, máximo]; no automático, o dos `dados` ([menor, maior], que quem
 * chama já tirou das séries no tipo do eixo) — no linear com o zero dentro, como hoje; no log, sem ponta ≤ 0 (sem
 * valor positivo, [1, 10]).
 */
export function dominioDaEscala(e: EscalaDoGrafico, dados: readonly [number, number]): [number, number] {
  if (e.limites !== 'auto') return [e.limites.min, e.limites.max];
  const [lo, hi] = dados;
  if (e.tipo === 'log') {
    if (!(lo > 0) || !(hi > 0) || !Number.isFinite(lo) || !Number.isFinite(hi)) return [1, 10];
    return [lo, hi];
  }
  return [Math.min(lo, 0), Math.max(hi, 0)];
}

/**
 * As marcas do eixo: com o passo digitado (só no linear), os múltiplos dele dentro do domínio (no máximo
 * `DIVISOES_MAXIMAS` + 1; acima disso, as de hoje); sem ele, as de hoje (`marcas`).
 */
export function marcasDaEscala(e: EscalaDoGrafico, dominio: readonly [number, number], alvo = 5): number[] {
  const lo = Math.min(dominio[0], dominio[1]);
  const hi = Math.max(dominio[0], dominio[1]);
  if (e.tipo === 'linear' && e.passo !== null && e.passo > 0 && Number.isFinite(lo) && Number.isFinite(hi)) {
    const passo = e.passo;
    const primeiro = Math.ceil(lo / passo - 1e-9);
    const ultimo = Math.floor(hi / passo + 1e-9);
    if (ultimo - primeiro <= DIVISOES_MAXIMAS) {
      const casas = Math.max(0, Math.min(12, -Math.floor(Math.log10(passo)) + 2));
      const saida: number[] = [];
      for (let k = primeiro; k <= ultimo; k++) {
        const v = Number((k * passo).toFixed(casas));
        saida.push(v === 0 ? 0 : v);
      }
      return saida;
    }
  }
  return marcas(e.tipo, [lo, hi], alvo);
}

/** Uma escala lida do aparelho, conferida campo a campo; null se não serve. */
function escalaLida(x: unknown, grandeza: string): EscalaDoGrafico | null {
  if (!x || typeof x !== 'object') return null;
  const o = x as Record<string, unknown>;
  const tipo = o.tipo === 'log' ? 'log' : o.tipo === 'linear' ? 'linear' : null;
  if (!tipo) return null;
  let limites: EscalaDoGrafico['limites'] = 'auto';
  if (o.limites !== 'auto') {
    const l = o.limites as Record<string, unknown> | null;
    if (!l || typeof l.min !== 'number' || typeof l.max !== 'number') return null;
    limites = { min: l.min, max: l.max };
  }
  const passo = o.passo === null || o.passo === undefined ? null : typeof o.passo === 'number' ? o.passo : NaN;
  const e: EscalaDoGrafico = { tipo, limites, passo };
  return validarEscala(e, grandeza) === null ? e : null;
}

/** As escalas guardadas no aparelho (as que não servem ficam de fora); sem aparelho, nenhuma. */
export function lerEscalas(chave: string): Record<string, EscalaDoGrafico> {
  const saida: Record<string, EscalaDoGrafico> = {};
  try {
    const bruto = localStorage.getItem(chave);
    if (!bruto) return saida;
    const x = JSON.parse(bruto) as unknown;
    if (!x || typeof x !== 'object' || Array.isArray(x)) return saida;
    for (const [g, v] of Object.entries(x as Record<string, unknown>)) {
      const e = escalaLida(v, g);
      if (e && !escalaEhAutomatica(e)) saida[g] = e;
    }
  } catch { /* o aparelho não guarda (ou guardou outra coisa): todas no automático */ }
  return saida;
}

/**
 * A chave das escalas de quem está na sessão: `<base>.<id>` — no aparelho compartilhado (os celulares do laboratório),
 * cada um com as suas (spec §4.8, «no aparelho (por usuário)»). Sem quem, a base.
 */
export function chaveDasEscalas(base: string, quem: string | null | undefined): string {
  return quem ? `${base}.${quem}` : base;
}

/**
 * As escalas de quem está na sessão, na chave dele (`chaveDasEscalas`). A chave sem o id nasceu e morreu na onda 347, sem
 * ser publicada: não há o que levar dela (revisão final da 347).
 */
export function lerEscalasDe(base: string, quem: string | null | undefined): Record<string, EscalaDoGrafico> {
  return lerEscalas(chaveDasEscalas(base, quem));
}

/** Guarda as escalas no aparelho (sem nenhuma, tira a chave); sem aparelho, valem até sair da tela. */
export function gravarEscalas(chave: string, e: Readonly<Record<string, EscalaDoGrafico>>): void {
  try {
    if (Object.keys(e).length === 0) localStorage.removeItem(chave);
    else localStorage.setItem(chave, JSON.stringify(e));
  } catch { /* sem memória no aparelho: vale até sair */ }
}

/** As escalas com a de `grandeza` trocada (null, ou a automática: volta ao automático). */
export function comEscala(
  escalas: Readonly<Record<string, EscalaDoGrafico>>, grandeza: string, e: EscalaDoGrafico | null,
): Record<string, EscalaDoGrafico> {
  const saida = { ...escalas };
  if (!e || escalaEhAutomatica(e)) delete saida[grandeza];
  else saida[grandeza] = e;
  return saida;
}

/**
 * O número digitado no padrão brasileiro («1.234,5», «0,25», «−3», «5»): NaN quando não é número. O ponto é o
 * dos milhares; a vírgula, a decimal (um ponto sozinho sem vírgula, como em «2.5», também vale como decimal).
 */
export function numeroDigitado(texto: string): number {
  const t = texto.trim().replace(/\s/g, '').replace('−', '-');
  if (!t) return NaN;
  let normal: string;
  if (t.includes(',')) normal = t.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(t)) normal = t.replace(/\./g, '');
  else normal = t;
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(normal)) return NaN;
  return Number(normal);
}
